<template>
  <Vue2Pdf :filename="filename" :manual-pagination="false" :paginate="true" :margin="[.3, 0]">
    <div class="px-2 text-sm mx-10 bg-white">
      <div class="flex items-center border-b-2 border-gray-300 pb-3 mb-4">
        <div class="flex-auto">
          <!-- <h1 class="text-4xl font-bold w-1/3 md:w-1/6">Dashboard</h1> -->
          <img :src="app.logo_b" :alt="`${app.name} logo`" width="120" height="28" />
        </div>
        <div class="flex-auto flex items-strech gap-8 justify-end">
          <div class="flex items-center border border-blue-400 px-2">
            <date-range-picker ref="picker" v-model="dateRange" :ranges="ranges" :auto-apply="true">
              <template #input="picker">
                <span>{{ getDate(picker.startDate) }} - {{ getDate(picker.endDate) }}</span>
              </template>
              <!--    footer slot-->
              <template #footer="data">
                <div class="flex items-center gap-4 justify-end my-2 py-1 px-2 text-sm">
                  <div>
                    {{ data.rangeText }}
                  </div>
                  <div class="flex items-stretch justify-around gap-2">
                    <button type="button" class="border border-blue-400 py-1 px-2" @click="data.clickCancel">Cancel</button>
                    <button v-if="! data.in_selection" type="button" class="btn-blue py-1 px-2" @click="data.clickApply">Apply</button>
                  </div>
                </div>
              </template>
            </date-range-picker>
            <icon name="calendar" class="w-4 h-4" />
          </div>
        </div>
      </div>
      <!-- Title -->

      <div class="flex items-center justify-around mb-4 text-center">
        <div class="px-8 py-4  bg-blue-800 text-white">
          <p class="font-bold mb-4 h-6">Projects</p>
          <p class="font-bold text-2xl">{{ metrics.count }}</p>
        </div>
        <div class="px-8 py-4  bg-blue-800 text-white">
          <p class="font-bold mb-4 h-6">Quotes <br /> Pending</p>
          <p class="font-bold text-2xl">{{ metrics.pending }}</p>
        </div>
        <div class="px-8 py-4  bg-blue-800 text-white">
          <p class="font-bold mb-4 h-6">Quotes <br /> Won</p>
          <p class="font-bold text-2xl">{{ metrics.won }}</p>
        </div>
        <div class="px-8 py-4  bg-blue-800 text-white">
          <p class="font-bold mb-4 h-6">Closing <br /> Rate</p>
          <p class="font-bold text-2xl">{{ metrics.rate }}%</p>
        </div>
      </div>
      <!-- metrics -->

      <div class="flex items-center justify-between border-b-2 border-gray-300 pb-2 mb-2">
        <div class="flex items-center w-1/3">
          <label for="group_by" class="form-label w-1/2 m-0 text-lg">Group by:</label>
          <select id="group_by" v-model="form.group_by" name="group_by" class="form-select" @change="search">
            <option value="date">Date</option>
            <option value="company">Company</option>
            <option value="owner">Project Owner</option>
            <option value="title">Project Name</option>
            <option value="status">Project Status</option>
            <option value="region">Project Region</option>
          </select>
        </div>
      </div>
      <!-- actions -->

      <div class="mb-2">
        <bar
          id="my-chart-id"
          :options="chartOptions"
          :data="chartData"
          :style="{height: '200px'}"
        />
      </div>
      <!-- Chart -->

      <table class="table-auto w-full whitespace-nowrap">
        <thead>
          <tr class="">
            <th width="5%" class="text-left px-2 py-1">Quote</th>
            <th class="text-left py-1">Project</th>
            <th class="text-left py-1">City</th>
            <th class="text-left py-1">Company</th>
            <th width="10%" class="text-center py-1">Quote Value</th>
            <th width="10%" class="text-center py-1">Status</th>
            <th width="10%" class="text-left py-1">Owner</th>
            <th width="5%" class="text-center py-1">Created on</th>
          </tr>
        </thead>

        <tbody>
          <tr class="bg-blue-800 text-white font-bold">
            <td class="py-1 text-right" colspan="5">${{ metrics.total }}</td>
            <td class="py-1 text-center" colspan="2">{{ metrics.rate }}% Closing Rate</td>
            <td class="py-1 pr-14 text-right">Total {{ metrics.count }} Quotes</td>
          </tr>
        </tbody>

        <tbody v-for="group in projects" :key="group.key">
          <tr class="bg-gray-100 border border-gray-200">
            <td colspan="4" class="py-1 pl-2"> {{ form.group_by == 'date' ? group.items[0].date :group.key }}</td>
            <td class="py-1 text-right">${{ group.total_str }}</td>
            <td class="py-1 text-center" colspan="2">{{ group.rate }}% Closing Rate</td>
            <td class="py-1 pr-10 text-right">
              <button type="button" @click="group.collapse = ! group.collapse">
                Total {{ group.count }} Quotes
                <icon :name="group.collapse ? 'cheveron-down' : 'cheveron-up'" class="w-4 h-4 inline-flex" />
              </button>
            </td>
          </tr>
          <tr v-for="p in group.items" v-show="! group.collapse " :key="p.id" class="border border-t-0 border-gray-100 hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="py-1 pl-2 ">
              <Link class="text-center focus:text-blue-500 font-bold" :href="`/projects/${p.id}`">
                {{ p.quote }}
              </Link>
            </td>
            <td class="py-1">
              <Link class=" text-left focus:text-blue-500" :href="`/projects/${p.id}`">
                <div class="whitespace-normal">{{ p.title }}</div>
              </Link>
            </td>
            <td class="py-1"><div class="whitespace-normal text-left">{{ p.city }}</div></td>
            <td class="py-1"><div class="whitespace-normal text-left">{{ p.company }}</div></td>
            <td class="py-1 text-right">${{ p.total_str }}</td>
            <td class="py-1 capitalize text-center">{{ p.status }}</td>
            <td class="py-1"><div class="whitespace-normal text-left">{{ p.owner }}</div></td>
            <td class="py-1 pr-2 text-right">{{ p.created_on }}</td>
          </tr>
          <tr><td colspan="8" class="py-1">&nbsp;</td></tr>
        </tbody>
      </table>
      <!-- Table -->
    </div>
  </Vue2Pdf>
</template>

<script>
import { Link } from '@inertiajs/vue3'
import pickBy from 'lodash/pickBy'
import Icon from '@/Shared/Icon'
import Vue2Pdf from '@/Shared/Vue2Pdf'

import DateRangePicker from 'daterange-picker-vue3'

import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

import dayjs from'dayjs'


export default {
  components: {
    Link,
    Icon,
    Bar,
    DateRangePicker,
    Vue2Pdf,
  },
  props: {
    app      : Object,
    members  : Array,
    companies: Array,
    titles   : Array,
    owners   : Array,
    statuses : Array,
    filters  : Object,
    projects : Object,
    metrics  : Object,
    chart    : Object,
    filename  : String,
  },
  data () {
    return {
      expandGroups: false,
      showFilters : false,
      dateRange: {
        startDate: this.filters.start,
        endDate  : this.filters.end,
      },
      form  : {
        members   : this.filters.members,
        titles    : this.filters.titles,
        companies : this.filters.companies,
        statuses  : this.filters.statuses,
        owners    : this.filters.owners,
        duplicated: this.filters.duplicated,
        closed    : this.filters.closed,
        group_by  : this.filters.groupBy,
        start     : this.filters.start,
        end       : this.filters.end,
      },
    }
  },
  computed: {
    chartData () {
      return {
        labels: this.chart.labels,
        datasets: [
          {
            label: 'Total Quotes',
            backgroundColor: '#A1A1A1',
            hoverBackgroundColor: '#002B56',
            barThickness: 10,
            maxBarThickness: 20,
            data: this.chart.count,
          },
        ],
      }
    },
    chartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 2,
        scales: {
          x: {
            grid: { display: false },
            border: {color: '#002B56'},
          },
          y:{
            grid  : { color: '#F2F2F2'},
            ticks : { color: '#002B56'},
            border: {color: '#002B56'},
          },
        },
      }
    },
    ranges () {
      let today = new Date()
      today.setHours(0, 0, 0, 0)

      let yesterday = new Date()
      yesterday.setDate(today.getDate() - 1)
      yesterday.setHours(0, 0, 0, 0)

      let last7days = new Date
      last7days.setDate(today.getDate() - 6)
      last7days.setHours(0, 0, 0, 0)

      let thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1)
      thisMonthStart.setHours(0, 0, 0, 0)

      let thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      thisMonthEnd.setHours(0, 0, 0, 0)

      return {
        'Today': [today, today],
        'Yesterday': [yesterday, yesterday],
        'Last 7 days' : [today, last7days],
        'This month': [thisMonthStart, thisMonthEnd],
        'Last month': [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)],
      }

    },
    request () {
      let data = pickBy(this.form)

      if (data.members) {
        data.members = data.members.map(el =>  el.value)
      }

      if (data.companies) {
        data.companies = data.companies.map(el =>  el.value)
      }

      if (data.titles) {
        data.titles = data.titles.map(el =>  el.value)
      }

      if (data.owners) {
        data.owners = data.owners.map(el =>  el.value)
      }

      if (data.statuses) {
        data.statuses = data.statuses.map(el =>  el.value)
      }

      return data
    },
  },
  methods: {
    getDate(date) {
      return dayjs(date).format('dddd, MMM DD')
    },
  },
}
</script>

<style src="daterange-picker-vue3/dist/daterange-picker-vue3.css"></style>z
<style>
.reportrange-text {
  border: none !important;
}

thead { display: table-header-group; }
tfoot { display: table-row-group;}
tr { page-break-inside: avoid; }

.table-block tr {
    page-break-after: always!important;
    page-break-before: always!important;
    page-break-inside: auto!important;
}
</style>
